/**
 * Edge Function transport for BYOK.
 *
 * Token is injected via `setByokToken(token)` — call this from the
 * `useSyncByokToken()` hook in any component that renders BYOK UI.
 * This replaces the old `supabase.auth.getSession()` dependency.
 */

import { ApiErrorException } from "@/lib/api/errors";

let _token: string | null = null;
let _tokenProvider: (() => Promise<string | null>) | null = null;

/**
 * Called from useSyncByokToken() to set a static token.
 * Prefer setByokTokenProvider — Clerk session tokens expire (~60s TTL),
 * so a cached static token goes stale and later requests 401.
 */
export function setByokToken(token: string | null): void {
  _token = token;
}

/**
 * Register a provider that returns a *fresh* token on every request.
 * Clerk's session.getToken() returns a cached token while valid and
 * transparently refreshes when expired, so calling it per-request keeps
 * test/delete/register working long after the page first loaded.
 */
export function setByokTokenProvider(
  provider: (() => Promise<string | null>) | null,
): void {
  _tokenProvider = provider;
}

async function resolveToken(): Promise<string | null> {
  if (_tokenProvider) {
    const fresh = await _tokenProvider();
    if (fresh) _token = fresh; // keep static copy in sync as fallback
    return fresh;
  }
  return _token;
}

function functionsBase(): string {
  const url = import.meta.env.VITE_LLM_PROXY_URL as string | undefined;
  if (!url) {
    // Fallback: derive from VITE_SUPABASE_URL
    const base = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    if (!base) throw new Error("VITE_SUPABASE_URL is not configured");
    return base.replace(/\/$/, "") + "/functions/v1";
  }
  return url.replace(/\/llm-proxy\/?$/, "");
}

async function send(
  fn: string,
  path: string,
  init: RequestInit,
  token: string,
): Promise<Response> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string> | undefined),
  };
  return fetch(`${functionsBase()}/${fn}${path}`, { ...init, headers });
}

export async function byokFetch<T>(
  fn: "byok-keys" | "byok-health",
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = await resolveToken();
  if (!token) {
    throw new Error("No active session — sign in to manage API keys.");
  }

  let res = await send(fn, path, init, token);

  // Self-healing: a 401 means the token was rejected (expired in flight,
  // clock skew, mid-request rotation). Grab a guaranteed-fresh token and
  // retry exactly once so the user never sees a transient auth error.
  if (res.status === 401 && _tokenProvider) {
    const fresh = await _tokenProvider();
    if (fresh && fresh !== token) {
      _token = fresh;
      res = await send(fn, path, init, fresh);
    }
  }

  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      /* ignore */
    }
    throw new ApiErrorException({
      kind: "server",
      status: res.status,
      message: `byok ${fn}${path} failed (${res.status})`,
      cause: detail,
    });
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}
